import { ComplianceCaseTypeEnum } from "~/graphql/supergraphEnums";
import { isEntityClientOnboardingCaseType } from "../ComplianceTable/CaseTypeRenderer/utils";

/**
 * Case types that use the new escalation revamp flow.
 * These case types show escalation controls in profiles table and use the revamped UI.
 */
export const ESCALATION_REVAMP_CASE_TYPES: readonly string[] = [
  ComplianceCaseTypeEnum.InvestorOnboarding,
  ComplianceCaseTypeEnum.PaymentInstructionChangeReview,
  ComplianceCaseTypeEnum.Remediation,
  ComplianceCaseTypeEnum.EntityOnboarding,
  ComplianceCaseTypeEnum.ClientOnboarding,
];

export const isEscalationRevampCaseType = (caseType?: string | null): boolean =>
  !!caseType && ESCALATION_REVAMP_CASE_TYPES.includes(caseType);

/** An escalation is Internal when its source case is an entity/client onboarding case, otherwise External. */
export const isInternalEscalationSource = (caseType?: string | null): boolean =>
  !!caseType && isEntityClientOnboardingCaseType(caseType as ComplianceCaseTypeEnum);

export const INTERNAL_ESCALATION_TYPE_LABEL = "Internal";
export const EXTERNAL_ESCALATION_TYPE_LABEL = "External";

export const escalationTypeLabel = (sourceCaseType?: string | null): string =>
  isInternalEscalationSource(sourceCaseType)
    ? INTERNAL_ESCALATION_TYPE_LABEL
    : EXTERNAL_ESCALATION_TYPE_LABEL;

// For now we are hard coding the available programs. Once we handle dynamically selecting programs,
// we'll need to pull this list from the server
export enum Programs {
  Screening = 1,
  KYC = 2,
  US_AML = 3,
  CAYMANS_AML = 4,
  LUXEMBOURG_AML = 5,
}

export const MAIN_SERVICE = "main";

/**
 * Union type for all valid program names in the compliance system
 */
export type ProgramName =
  | "Screening"
  | "KYC"
  | "AML (US)"
  | "AML (Caymans)"
  | "AML (Luxembourg)"
  | "Payments (CRB)"
  | "None";

/**
 * These are the external object types for the Compliance service. These are representations
 * of types that are implicitly in Compliance (there are no accounts, prospects, subscriptions etc...
 * in compliance). However, we track these relationships to objects found in other services.
 *
 * This enumeration is that set of values the Compliance Service expects to see but are stored
 * on other systems. The initial set all came from the "main" service.
 * Typically, these types will be paired with an ID and a service name.
 *
 * Note: 'objectType' string field has been completely removed and replaced with this enum
 * as part of a gradual deprecation effort. The backend may still support 'objectType' temporarily,
 * but the frontend uses only 'objectTypeEnum' for all compliance-related code.
 */
export enum ObjectTypeEnum {
  Account = "account",
  Arena = "arena",
  Prospect = "prospect",
  Subscription = "subscription",
  User = "user",
}
